import React, { useState, useMemo } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRBAC } from '../../contexts/RBACContext';
import { BUILDINGS_CONFIG } from '../../contexts/BookingContext';
import { useClaims } from '../../contexts/ClaimsContext';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import AnomalyAlertSystem from './components/AnomalyAlertSystem';

// Build BUILDINGS from BUILDINGS_CONFIG for backward compat
const BUILDINGS = Object.fromEntries(
  Object.entries(BUILDINGS_CONFIG)?.map(([k, v]) => [k, { floors: v?.floors?.map(f => ({ floor: f?.floor, rooms: f?.rooms })) }])
);

const STATUS_CONFIG = {
  'confirmed-stay': { labelKey: 'confirmedStay', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  'active-reservation': { labelKey: 'activeReservation', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  'completed-stay': { labelKey: 'completedStay', color: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
  active: { labelKey: 'confirmedStay', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  pending: { labelKey: 'activeReservation', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  'checked-out': { labelKey: 'completedStay', color: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
};

const GUEST_STATUS_CONFIG = {
  'awaiting-arrival': { color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500', icon: 'Clock' },
  'checked-in': { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', icon: 'UserCheck' },
  'checked-out': { color: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400', icon: 'LogOut' },
  'extended': { color: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500', icon: 'CalendarPlus' },
  'early-departure': { color: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'bg-rose-500', icon: 'AlertCircle' },
};

const ACCOMMODATION_TYPES = [
  { value: 'long-term', labelEn: 'Long Term Contract', labelFr: 'Contrat long terme' },
  { value: 'short-term', labelEn: 'Short Term Accommodation', labelFr: 'Hébergement court terme' },
];

const FINANCIAL_STATUS_OPTIONS = [
  { value: 'covered', labelEn: 'Covered by Senghor', labelFr: 'Pris en charge par Senghor' },
  { value: 'not-covered', labelEn: 'Not Covered by Senghor', labelFr: 'Non pris en charge par Senghor' },
];

const today = new Date()?.toISOString()?.split('T')?.[0];

const emptyCheckIn = {
  residentName: '', residentId: '', guestId: '', passportId: '', phone: '', nationality: '',
  email: '', building: '', floor: '', room: '',
  checkIn: today, checkOut: '', notes: '', accommodationType: '', guestStatus: '',
  department: '', financialStatus: '', gender: '', residentCategory: '',
};

const emptyCheckOut = {
  stayId: '', actualCheckOut: today, keyReturned: false, roomCheckCompleted: false, notes: '',
  claimEnabled: false,
  claimDescription: '', claimPhotos: [], claimPhotoFiles: [],
};

// ─── Date overlap check ───────────────────────────────────────────────────────
// Returns true if [newIn, newOut) overlaps with [existIn, existOut)
function datesOverlap(newIn, newOut, existIn, existOut) {
  if (!newIn || !newOut || !existIn || !existOut) return false;
  return newIn < existOut && newOut > existIn;
}

// ─── Compute availability for a room given date range ────────────────────────
function getRoomAvailabilityStatus(roomNumber, arrivalDate, departureDate, stays, rooms, roomTransfers) {
  if (!arrivalDate || !departureDate) return { available: false, reason: 'dates-required' };

  // 1. Check rooms table for maintenance
  const roomRecord = rooms?.find(r => r?.room_number === roomNumber);
  if (roomRecord?.cleaning_status === 'maintenance') {
    return { available: false, reason: 'maintenance', label: 'Maintenance' };
  }

  // 2. Check active room transfers (room blocked during transfer period)
  const hasTransferConflict = roomTransfers?.some(t => {
    if (t?.to_room !== roomNumber && t?.from_room !== roomNumber) return false;
    const transferDate = t?.transfer_date;
    if (!transferDate) return false;
    // Block room on transfer date ± 1 day
    const tIn = transferDate;
    const tOut = transferDate;
    return datesOverlap(arrivalDate, departureDate, tIn, tOut);
  });
  if (hasTransferConflict) {
    return { available: false, reason: 'transfer', label: 'Room Transfer' };
  }

  // 3. Check stays for overlapping bookings
  const conflictingStay = stays?.find(s => {
    const sRoom = s?.room_number || s?.room;
    if (sRoom !== roomNumber) return false;
    // Skip completed stays
    if (s?.status === 'completed-stay' || s?.status === 'checked-out') return false;
    const sIn = s?.check_in_date || s?.checkIn;
    const sOut = s?.check_out_date || s?.checkOut;
    return datesOverlap(arrivalDate, departureDate, sIn, sOut);
  });

  if (conflictingStay) {
    const status = conflictingStay?.status;
    if (status === 'confirmed-stay' || status === 'active') {
      return { available: false, reason: 'occupied', label: 'Occupied', conflictStay: conflictingStay };
    }
    if (status === 'active-reservation' || status === 'pending') {
      return { available: false, reason: 'reserved', label: 'Reserved', conflictStay: conflictingStay };
    }
    return { available: false, reason: 'conflict', label: 'Conflict', conflictStay: conflictingStay };
  }

  return { available: true, reason: 'available', label: 'Available' };
}

// ─── Guest Detail Modal ───────────────────────────────────────────────────────
function GuestDetailModal({ guest, onClose, language, guestStatusLabels, statusLabels, financialStatusLabel, accommodationLabel }) {
  if (!guest) return null;

  const gsCfg = GUEST_STATUS_CONFIG?.[guest?.guestStatus] || GUEST_STATUS_CONFIG?.['checked-out'];
  const stCfg = STATUS_CONFIG?.[guest?.status] || STATUS_CONFIG?.['completed-stay'];

  const DetailRow = ({ icon, label, value, valueClass = '' }) => (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-0">
      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
        <Icon name={icon} size={15} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground font-medium">{label}</p>
        <p className={`text-sm font-semibold text-foreground mt-0.5 break-words ${valueClass}`}>{value || '—'}</p>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        style={{ animation: 'modalIn 0.22s cubic-bezier(0.34,1.56,0.64,1) both' }}
        onClick={e => e?.stopPropagation()}
      >
        <div className="sticky top-0 bg-card border-b border-border rounded-t-2xl px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Icon name="User" size={22} className="text-primary" />
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground">{guest?.residentName}</h3>
              <p className="text-xs text-muted-foreground">{guest?.residentId}{guest?.guestId ? ` · Guest ID: ${guest?.guestId}` : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-medium ${gsCfg?.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${gsCfg?.dot}`} />
              {guestStatusLabels?.[guest?.guestStatus] || guest?.guestStatus}
            </span>
            <button onClick={onClose} className="w-8 h-8 rounded-lg bg-muted hover:bg-border flex items-center justify-center transition-colors ml-2">
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
        <div className="px-6 py-4">
          <div className="flex flex-wrap gap-2 mb-5">
            <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-medium ${stCfg?.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${stCfg?.dot}`} />
              {statusLabels?.[stCfg?.labelKey] || guest?.status}
            </span>
            {guest?.accommodationType && (
              <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-medium ${guest?.accommodationType === 'long-term' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>
                <Icon name={guest?.accommodationType === 'long-term' ? 'FileText' : 'CalendarDays'} size={11} />
                {accommodationLabel(guest?.accommodationType)}
              </span>
            )}
            {guest?.financialStatus && (
              <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-medium ${guest?.financialStatus === 'covered' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-rose-100 text-rose-700 border-rose-200'}`}>
                <Icon name="CreditCard" size={11} />
                {financialStatusLabel(guest?.financialStatus)}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            <div>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">{language === 'fr' ? 'Identité' : 'Identity'}</p>
              <DetailRow icon="User" label={language === 'fr' ? 'Nom complet' : 'Full Name'} value={guest?.residentName} />
              <DetailRow icon="Hash" label={language === 'fr' ? 'ID Résident' : 'Resident ID'} value={guest?.residentId} />
              <DetailRow icon="BadgeCheck" label={language === 'fr' ? 'ID Hôte (Guest ID)' : 'Guest ID'} value={guest?.guestId} />
              <DetailRow icon="CreditCard" label={language === 'fr' ? 'Passeport / Pièce d\'identité' : 'Passport / National ID'} value={guest?.passportId} />
              <DetailRow icon="Globe" label={language === 'fr' ? 'Nationalité' : 'Nationality'} value={guest?.nationality} />
              <DetailRow icon="Phone" label={language === 'fr' ? 'Téléphone' : 'Phone Number'} value={guest?.phone} />
              <DetailRow icon="Mail" label={language === 'fr' ? 'Email' : 'Email'} value={guest?.email} />
            </div>
            <div>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">{language === 'fr' ? 'Séjour' : 'Stay Details'}</p>
              <DetailRow icon="Home" label={language === 'fr' ? 'Numéro de chambre' : 'Room Number'} value={`${guest?.room} (${guest?.building})`} />
              <DetailRow icon="LogIn" label={language === 'fr' ? 'Date d\'arrivée' : 'Check-in Date'} value={guest?.checkIn} />
              <DetailRow icon="LogOut" label={language === 'fr' ? 'Date de départ' : 'Check-out Date'} value={guest?.checkOut} />
              <DetailRow icon="Building2" label={language === 'fr' ? 'Département' : 'Department'} value={guest?.department} />
              <DetailRow icon="CreditCard" label={language === 'fr' ? 'Statut de paiement' : 'Payment Status'} value={financialStatusLabel(guest?.financialStatus)} valueClass={guest?.financialStatus === 'covered' ? 'text-emerald-600' : guest?.financialStatus === 'not-covered' ? 'text-rose-600' : ''} />
              <DetailRow icon="ClipboardCheck" label={language === 'fr' ? 'Statut de réservation' : 'Reservation Status'} value={statusLabels?.[STATUS_CONFIG?.[guest?.status]?.labelKey] || guest?.status} />
            </div>
          </div>
          {guest?.notes && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <Icon name="StickyNote" size={13} />
                {language === 'fr' ? 'Notes / Demandes spéciales' : 'Notes / Special Requests'}
              </p>
              <p className="text-sm text-amber-800 leading-relaxed">{guest?.notes}</p>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end">
          <button onClick={onClose} className="flex items-center gap-2 px-5 py-2.5 bg-muted text-muted-foreground text-sm font-medium rounded-lg hover:bg-border transition-all">
            <Icon name="X" size={14} />
            {language === 'fr' ? 'Fermer' : 'Close'}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.92) translateY(16px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ─── Step Indicator ───────────────────────────────────────────────────────────
function StepIndicator({ currentStep, language }) {
  const steps = [
    { num: 1, labelEn: 'Resident Info', labelFr: 'Infos résident', icon: 'User' },
    { num: 2, labelEn: 'Arrival Date', labelFr: 'Date d\'arrivée', icon: 'LogIn' },
    { num: 3, labelEn: 'Departure Date', labelFr: 'Date de départ', icon: 'LogOut' },
    { num: 4, labelEn: 'Room Selection', labelFr: 'Choix chambre', icon: 'Home' },
    { num: 5, labelEn: 'Confirm', labelFr: 'Confirmer', icon: 'CheckCircle' },
  ];
  return (
    <div className="flex items-center gap-0 mb-6 overflow-x-auto pb-1">
      {steps?.map((step, idx) => (
        <React.Fragment key={step?.num}>
          <div className="flex flex-col items-center min-w-[60px]">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
              currentStep > step?.num
                ? 'bg-emerald-500 border-emerald-500 text-white'
                : currentStep === step?.num
                ? 'bg-primary border-primary text-primary-foreground'
                : 'bg-muted border-border text-muted-foreground'
            }`}>
              {currentStep > step?.num
                ? <Icon name="Check" size={16} />
                : <Icon name={step?.icon} size={15} />
              }
            </div>
            <span className={`text-xs mt-1 text-center leading-tight font-medium ${
              currentStep === step?.num ? 'text-primary' : currentStep > step?.num ? 'text-emerald-600' : 'text-muted-foreground'
            }`}>
              {language === 'fr' ? step?.labelFr : step?.labelEn}
            </span>
          </div>
          {idx < steps?.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1 mb-5 transition-all ${currentStep > step?.num ? 'bg-emerald-400' : 'bg-border'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Room Availability Card ───────────────────────────────────────────────────
function RoomAvailabilityCard({ roomNumber, availStatus, selected, onSelect, language }) {
  const statusColors = {
    available: 'border-emerald-300 bg-emerald-50 hover:border-emerald-500 cursor-pointer',
    occupied: 'border-red-200 bg-red-50 opacity-70 cursor-not-allowed',
    reserved: 'border-amber-200 bg-amber-50 opacity-70 cursor-not-allowed',
    maintenance: 'border-orange-200 bg-orange-50 opacity-70 cursor-not-allowed',
    transfer: 'border-purple-200 bg-purple-50 opacity-70 cursor-not-allowed',
    conflict: 'border-rose-200 bg-rose-50 opacity-70 cursor-not-allowed',
  };
  const statusBadge = {
    available: 'bg-emerald-100 text-emerald-700',
    occupied: 'bg-red-100 text-red-700',
    reserved: 'bg-amber-100 text-amber-700',
    maintenance: 'bg-orange-100 text-orange-700',
    transfer: 'bg-purple-100 text-purple-700',
    conflict: 'bg-rose-100 text-rose-700',
  };
  const statusIcon = {
    available: 'CheckCircle',
    occupied: 'UserCheck',
    reserved: 'Clock',
    maintenance: 'Wrench',
    transfer: 'ArrowLeftRight',
    conflict: 'AlertCircle',
  };

  const reason = availStatus?.reason || 'available';
  const isAvailable = availStatus?.available;
  const conflict = availStatus?.conflictStay;

  return (
    <div
      onClick={() => isAvailable && onSelect(roomNumber)}
      className={`rounded-xl border-2 p-3 transition-all ${statusColors?.[reason] || statusColors?.available} ${selected ? 'ring-2 ring-primary ring-offset-1 border-primary bg-primary/5' : ''}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-sm font-bold text-foreground">{roomNumber}</span>
        {selected && <Icon name="CheckCircle" size={16} className="text-primary" />}
      </div>
      <div className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge?.[reason] || statusBadge?.available}`}>
        <Icon name={statusIcon?.[reason] || 'Circle'} size={10} />
        {availStatus?.label || (language === 'fr' ? 'Disponible' : 'Available')}
      </div>
      {conflict && (
        <p className="text-xs text-muted-foreground mt-1 truncate">
          {conflict?.resident_name || conflict?.residentName} · {conflict?.check_in_date || conflict?.checkIn} → {conflict?.check_out_date || conflict?.checkOut}
        </p>
      )}
    </div>
  );
}

export default function ResidentCheckin() {
  const { language } = useLanguage();
  const { isAdmin, isViewer } = useRBAC();
  const { addClaim } = useClaims();
  const { user } = useAuth();
  const realtime = useRealtime();

  // Use Supabase-backed stays only
  const stays = realtime?.stays?.map(s => ({
    ...s,
    id: s?.id,
    residentName: s?.resident_name || s?.residentName,
    residentId: s?.resident_id_code || s?.residentId,
    room: s?.room_number || s?.room,
    checkIn: s?.check_in_date || s?.checkIn,
    checkOut: s?.check_out_date || s?.checkOut,
    guestStatus: s?.guest_status || s?.guestStatus,
    accommodationType: s?.accommodation_type || s?.accommodationType,
    financialStatus: s?.financial_status || s?.financialStatus,
  })) || [];

  const rooms = realtime?.rooms || [];
  const roomTransfers = realtime?.roomTransfers || [];

  const [activeTab, setActiveTab] = useState(isAdmin ? 'checkin' : 'tracker');
  const [checkInForm, setCheckInForm] = useState(emptyCheckIn);
  const [checkOutForm, setCheckOutForm] = useState(emptyCheckOut);
  const [checkInSuccess, setCheckInSuccess] = useState(null);
  const [checkOutSuccess, setCheckOutSuccess] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [guestStatusFilter, setGuestStatusFilter] = useState('all');
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [lastClaim, setLastClaim] = useState(null);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkInError, setCheckInError] = useState(null);
  const [checkOutError, setCheckOutError] = useState(null);

  // ── Step-based check-in wizard state ──────────────────────────────────────
  const [checkInStep, setCheckInStep] = useState(1);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('available'); // 'all' | 'available'

  // ── Computed: available floors for selected building ──────────────────────
  const availableFloors = selectedBuilding
    ? BUILDINGS?.[selectedBuilding]?.floors?.map(f => f?.floor) ?? []
    : [];

  // ── Computed: all rooms in selected building with availability status ──────
  const buildingRoomsWithAvailability = useMemo(() => {
    if (!selectedBuilding || !checkInForm?.checkIn || !checkInForm?.checkOut) return [];
    const allRooms = BUILDINGS?.[selectedBuilding]?.floors?.flatMap(f => f?.rooms) ?? [];
    return allRooms?.map(roomNumber => {
      const availStatus = getRoomAvailabilityStatus(
        roomNumber,
        checkInForm?.checkIn,
        checkInForm?.checkOut,
        realtime?.stays || [],
        rooms,
        roomTransfers
      );
      return { roomNumber, availStatus };
    });
  }, [selectedBuilding, checkInForm?.checkIn, checkInForm?.checkOut, realtime?.stays, rooms, roomTransfers]);

  const availableRoomsCount = buildingRoomsWithAvailability?.filter(r => r?.availStatus?.available)?.length;
  const totalRoomsInBuilding = buildingRoomsWithAvailability?.length;

  // ── Length of stay calculation ─────────────────────────────────────────────
  const lengthOfStay = useMemo(() => {
    if (!checkInForm?.checkIn || !checkInForm?.checkOut) return null;
    const d1 = new Date(checkInForm?.checkIn);
    const d2 = new Date(checkInForm?.checkOut);
    const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : null;
  }, [checkInForm?.checkIn, checkInForm?.checkOut]);

  // ── Step validation ────────────────────────────────────────────────────────
  const canProceedStep1 = checkInForm?.residentName?.trim() && checkInForm?.email?.trim() && checkInForm?.gender;
  const canProceedStep2 = checkInForm?.checkIn;
  const canProceedStep3 = checkInForm?.checkOut && checkInForm?.checkOut > checkInForm?.checkIn;
  const canProceedStep4 = checkInForm?.room && checkInForm?.building && checkInForm?.accommodationType;

  const handleCheckInChange = (field, value) => {
    setCheckInForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'building') {
        updated.floor = '';
        updated.room = '';
        setSelectedBuilding(value);
      }
      if (field === 'floor') { updated.room = ''; }
      // When dates change, reset room selection to force re-validation
      if (field === 'checkIn' || field === 'checkOut') { updated.room = ''; }
      return updated;
    });
  };

  const handleBuildingSelect = (building) => {
    setSelectedBuilding(building);
    handleCheckInChange('building', building);
  };

  const handleRoomSelect = (roomNumber) => {
    setCheckInForm(prev => ({ ...prev, room: roomNumber }));
  };

  const handleNextStep = () => {
    setCheckInStep(prev => Math.min(prev + 1, 5));
  };

  const handlePrevStep = () => {
    setCheckInStep(prev => Math.max(prev - 1, 1));
  };

  const handleCheckInSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    setCheckInError(null);

    // Final availability validation before submit
    if (checkInForm?.room && checkInForm?.checkIn && checkInForm?.checkOut) {
      const finalCheck = getRoomAvailabilityStatus(
        checkInForm?.room,
        checkInForm?.checkIn,
        checkInForm?.checkOut,
        realtime?.stays || [],
        rooms,
        roomTransfers
      );
      if (!finalCheck?.available) {
        setCheckInError(
          language === 'fr'
            ? `La chambre ${checkInForm?.room} n'est plus disponible pour cette période. Raison: ${finalCheck?.label}. Veuillez sélectionner une autre chambre.`
            : `Room ${checkInForm?.room} is no longer available for this period. Reason: ${finalCheck?.label}. Please select another room.`
        );
        setSubmitting(false);
        return;
      }
    }

    const stayData = {
      ...checkInForm,
      status: 'confirmed-stay',
      guestStatus: 'checked-in',
      guestId: checkInForm?.guestId || `GID-${Date.now()?.toString()?.slice(-6)}`,
      gender: checkInForm?.gender || null,
      passportId: checkInForm?.passportId || null,
      residentCategory: checkInForm?.residentCategory || null,
    };

    if (realtime?.addStay) {
      const { data, error } = await realtime?.addStay(stayData);
      if (error) {
        console.error('Check-in Supabase error:', error?.message);
        setCheckInError(error?.message || 'Failed to save check-in. Please try again.');
        setSubmitting(false);
        return;
      }
      if (data) {
        const newStay = { ...stayData, id: data?.id, residentName: stayData?.residentName, room: stayData?.room };
        setCheckInSuccess(newStay);
        setCheckInForm(emptyCheckIn);
        setCheckInStep(1);
        setSelectedBuilding('');
        setEmailSending(true);
        // Trigger immediate refresh of all analytics after check-in
        setTimeout(() => {
          realtime?.refreshAll?.();
        }, 500);
        setTimeout(() => { setEmailSending(false); setEmailStatus('sent'); }, 1500);
      }
    } else {
      setCheckInError('Database connection unavailable. Please refresh the page.');
    }
    setSubmitting(false);
  };

  const handleCheckOutSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    setCheckOutError(null);
    const stay = stays?.find(s => s?.id === checkOutForm?.stayId);
    if (!stay) {
      setCheckOutError('Stay record not found. Please select a valid resident.');
      setSubmitting(false);
      return;
    }

    const claim = checkOutForm?.claimEnabled && checkOutForm?.claimDescription
      ? { stayId: stay?.id, room: stay?.room, guestName: stay?.residentName, description: checkOutForm?.claimDescription, photos: checkOutForm?.claimPhotos, reportDate: checkOutForm?.actualCheckOut }
      : null;

    if (claim) {
      await addClaim(claim);
      setLastClaim(claim);
    }

    if (realtime?.processCheckOut && typeof stay?.id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i?.test(stay?.id)) {
      const { error } = await realtime?.processCheckOut(stay?.id, checkOutForm?.actualCheckOut);
      if (error) {
        setCheckOutError(error?.message || 'Failed to process check-out. Please try again.');
        setSubmitting(false);
        return;
      }
    } else {
      setCheckOutError('Invalid stay record. Cannot process check-out.');
      setSubmitting(false);
      return;
    }

    setCheckOutSuccess({ ...stay, actualCheckOut: checkOutForm?.actualCheckOut, claim });
    setCheckOutForm(emptyCheckOut);
    setTimeout(() => {
      realtime?.refreshAll?.();
    }, 500);
    setSubmitting(false);
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e?.target?.files ?? []);
    const readers = files?.map(file => new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = ev => resolve({ url: ev?.target?.result, name: file?.name });
      reader.readAsDataURL(file);
    }));
    Promise.all(readers)?.then(results => {
      setCheckOutForm(prev => ({
        ...prev,
        claimPhotos: [...(prev?.claimPhotos ?? []), ...results?.map(r => r?.url)],
        claimPhotoFiles: [...(prev?.claimPhotoFiles ?? []), ...results?.map(r => r?.name)],
      }));
    });
  };

  const removePhoto = (idx) => {
    setCheckOutForm(prev => ({
      ...prev,
      claimPhotos: prev?.claimPhotos?.filter((_, i) => i !== idx),
      claimPhotoFiles: prev?.claimPhotoFiles?.filter((_, i) => i !== idx),
    }));
  };

  const handleDownloadClaimPDF = (claim) => {
    setPdfGenerating(true);
    setTimeout(() => {
      const content = `Claim Report\nDate: ${claim?.date}\nRoom: ${claim?.room}\nResident: ${claim?.residentName}\nDescription: ${claim?.description}`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `claim-report-${claim?.id}.txt`; a?.click();
      URL.revokeObjectURL(url);
      setPdfGenerating(false);
    }, 1000);
  };

  const activeStays = stays?.filter(s => s?.status === 'confirmed-stay' || s?.status === 'active-reservation' || s?.status === 'active' || s?.status === 'pending');

  const filteredStays = stays?.filter(s => {
    const matchStatus = statusFilter === 'all' || s?.status === statusFilter;
    const matchSearch = !searchQuery || s?.residentName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) || s?.room?.toLowerCase()?.includes(searchQuery?.toLowerCase()) || s?.residentId?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchStatus && matchSearch;
  });

  const filteredGuestStatus = stays?.filter(s => {
    const matchStatus = guestStatusFilter === 'all' || s?.guestStatus === guestStatusFilter;
    const matchSearch = !searchQuery || s?.residentName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) || s?.room?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchStatus && matchSearch;
  });

  const stats = {
    activeReservation: stays?.filter(s => s?.status === 'active-reservation')?.length,
    confirmedStay: stays?.filter(s => s?.status === 'confirmed-stay')?.length,
    completedStay: stays?.filter(s => s?.status === 'completed-stay')?.length,
  };

  const statusLabels = {
    confirmedStay: language === 'fr' ? 'Séjour confirmé' : 'Confirmed Stay',
    activeReservation: language === 'fr' ? 'Réservation active' : 'Active Reservation',
    completedStay: language === 'fr' ? 'Séjour terminé' : 'Completed Stay',
  };

  const guestStatusLabels = {
    'awaiting-arrival': language === 'fr' ? 'En attente d\'arrivée' : 'Awaiting Arrival',
    'checked-in': language === 'fr' ? 'Enregistré' : 'Checked In',
    'checked-out': language === 'fr' ? 'Parti' : 'Checked Out',
    'extended': language === 'fr' ? 'Séjour prolongé' : 'Stay Extended',
    'early-departure': language === 'fr' ? 'Départ anticipé' : 'Early Departure',
  };

  const accommodationLabel = (type) => {
    if (type === 'long-term') return language === 'fr' ? 'Contrat long terme' : 'Long Term Contract';
    if (type === 'short-term') return language === 'fr' ? 'Court terme' : 'Short Term';
    return '—';
  };

  const financialStatusLabel = (val) => {
    if (val === 'covered') return language === 'fr' ? 'Pris en charge par Senghor' : 'Covered by Senghor';
    if (val === 'not-covered') return language === 'fr' ? 'Non pris en charge par Senghor' : 'Not Covered by Senghor';
    return '—';
  };

  // ── Filtered rooms for step 4 ──────────────────────────────────────────────
  const displayedRooms = availabilityFilter === 'available'
    ? buildingRoomsWithAvailability?.filter(r => r?.availStatus?.available)
    : buildingRoomsWithAvailability;

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Guest Detail Modal */}
        {selectedGuest && (
          <GuestDetailModal
            guest={selectedGuest}
            onClose={() => setSelectedGuest(null)}
            language={language}
            guestStatusLabels={guestStatusLabels}
            statusLabels={statusLabels}
            financialStatusLabel={financialStatusLabel}
            accommodationLabel={accommodationLabel}
          />
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground font-heading">
              {language === 'fr' ? 'Enregistrement / Départ des résidents' : 'Resident Check-in / Check-out'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {language === 'fr' ? 'Gérer les arrivées, départs et séjours actifs des résidents' : 'Manage resident arrivals, departures, and active stays'}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-card border border-border rounded-lg px-3 py-2">
            <Icon name="Calendar" size={14} />
            <span>{new Date()?.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: language === 'fr' ? 'Séjours confirmés' : 'Confirmed Stays', value: stats?.confirmedStay, icon: 'UserCheck', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' },
            { label: language === 'fr' ? 'Réservations actives' : 'Active Reservations', value: stats?.activeReservation, icon: 'Clock', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
            { label: language === 'fr' ? 'Séjours terminés' : 'Completed Stays', value: stats?.completedStay, icon: 'LogOut', color: 'text-slate-600', bg: 'bg-slate-50 border-slate-200' },
          ]?.map((stat, i) => (
            <div key={i} className={`rounded-xl border p-4 ${stat?.bg}`}>
              <div className="flex items-center gap-3">
                <div className={`${stat?.color}`}><Icon name={stat?.icon} size={20} /></div>
                <div>
                  <div className={`text-2xl font-bold ${stat?.color}`}>{stat?.value}</div>
                  <div className="text-xs text-muted-foreground">{stat?.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Anomaly Alert System */}
        <AnomalyAlertSystem />

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-muted rounded-xl p-1 w-fit">
          {[
            { id: 'checkin', label: language === 'fr' ? 'Enregistrement' : 'Check-in', icon: 'LogIn', adminOnly: true },
            { id: 'checkout', label: language === 'fr' ? 'Départ' : 'Check-out', icon: 'LogOut', adminOnly: true },
            { id: 'guest-status', label: language === 'fr' ? 'Statut des hôtes' : 'Guest Status', icon: 'Users', adminOnly: false },
            { id: 'tracker', label: language === 'fr' ? 'Suivi des statuts' : 'Status Tracker', icon: 'ClipboardList', adminOnly: false },
          ]?.filter(tab => !tab?.adminOnly || isAdmin)?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => { setActiveTab(tab?.id); setCheckInSuccess(null); setCheckOutSuccess(null); setEmailStatus(null); setLastClaim(null); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === tab?.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={15} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Viewer read-only notice */}
        {isViewer && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-sky-50 dark:bg-sky-500/10 border border-sky-200 dark:border-sky-500/20 text-sky-700 dark:text-sky-300 text-sm">
            <Icon name="Eye" size={15} />
            <span>You have <strong>read-only</strong> access. Check-in and check-out actions are restricted to Admin users.</span>
          </div>
        )}

        {/* ─── CHECK-IN WIZARD ───────────────────────────────────────────────── */}
        {activeTab === 'checkin' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-card border border-border rounded-xl p-6">
              {/* Wizard header */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Icon name="LogIn" size={18} className="text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{language === 'fr' ? 'Nouvel enregistrement' : 'New Check-in'}</h3>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Processus guidé en 5 étapes avec validation de disponibilité' : '5-step guided process with availability validation'}</p>
                </div>
              </div>

              {/* Step Indicator */}
              <StepIndicator currentStep={checkInStep} language={language} />

              {/* Success Banner */}
              {checkInSuccess && (
                <div className="mb-4 flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <Icon name="CheckCircle" size={18} className="text-emerald-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-emerald-800">{language === 'fr' ? 'Enregistrement effectué avec succès !' : 'Check-in registered successfully!'}</p>
                    <p className="text-xs text-emerald-700 mt-0.5">{checkInSuccess?.residentName} — {language === 'fr' ? 'Chambre' : 'Room'} {checkInSuccess?.room} — {language === 'fr' ? 'Du' : 'From'} {checkInSuccess?.checkIn} {language === 'fr' ? 'au' : 'to'} {checkInSuccess?.checkOut}</p>
                    {checkInSuccess?.guestId && <p className="text-xs text-emerald-700 mt-0.5">Guest ID: {checkInSuccess?.guestId}</p>}
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                      <Icon name="RefreshCw" size={11} />
                      {language === 'fr' ? 'Statut mis à jour dans Room Management, Calendar, Dashboard et Resident Lifecycle.' : 'Status updated in Room Management, Calendar, Dashboard and Resident Lifecycle.'}
                    </p>
                    {emailSending && <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><Icon name="Loader" size={12} className="animate-spin" />{language === 'fr' ? 'Envoi de l\'e-mail...' : 'Sending email...'}</p>}
                    {emailStatus === 'sent' && <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><Icon name="Mail" size={12} />{language === 'fr' ? 'E-mail envoyé !' : 'Email sent!'}</p>}
                  </div>
                  <button onClick={() => { setCheckInSuccess(null); setEmailStatus(null); }} className="ml-auto text-emerald-500 hover:text-emerald-700"><Icon name="X" size={14} /></button>
                </div>
              )}

              {/* Error Banner */}
              {checkInError && (
                <div className="mb-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
                  <Icon name="AlertCircle" size={18} className="text-red-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">{language === 'fr' ? 'Erreur lors de l\'enregistrement' : 'Check-in failed'}</p>
                    <p className="text-xs text-red-700 mt-0.5">{checkInError}</p>
                  </div>
                  <button onClick={() => setCheckInError(null)} className="ml-auto text-red-400 hover:text-red-600"><Icon name="X" size={14} /></button>
                </div>
              )}

              {/* ── STEP 1: Resident Information ── */}
              {checkInStep === 1 && (
                <div className="space-y-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">1</div>
                    <h4 className="font-semibold text-foreground">{language === 'fr' ? 'Informations du résident' : 'Resident Information'}</h4>
                  </div>

                  {/* Full Name */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">{language === 'fr' ? 'Nom complet' : 'Full Name'} <span className="text-red-500">*</span></label>
                    <input required type="text" value={checkInForm?.residentName} onChange={e => handleCheckInChange('residentName', e?.target?.value)} placeholder="e.g. Ahmed Benali" className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" />
                  </div>

                  {/* Resident ID */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">{language === 'fr' ? 'ID Résident' : 'Resident ID'}</label>
                    <input type="text" value={checkInForm?.residentId} onChange={e => handleCheckInChange('residentId', e?.target?.value)} placeholder="e.g. RES-001" className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" />
                  </div>

                  {/* Passport */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5"><span className="flex items-center gap-1.5"><Icon name="CreditCard" size={12} />{language === 'fr' ? 'Passeport / Pièce d\'identité' : 'Passport / National ID'}</span></label>
                    <input type="text" value={checkInForm?.passportId} onChange={e => handleCheckInChange('passportId', e?.target?.value)} placeholder="e.g. AB1234567" className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" />
                  </div>

                  {/* Gender + Category */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5"><span className="flex items-center gap-1.5"><Icon name="Users" size={12} />{language === 'fr' ? 'Genre' : 'Gender'} <span className="text-red-500">*</span></span></label>
                      <select required value={checkInForm?.gender} onChange={e => handleCheckInChange('gender', e?.target?.value)} className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all">
                        <option value="">{language === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                        <option value="male">{language === 'fr' ? 'Homme' : 'Male'}</option>
                        <option value="female">{language === 'fr' ? 'Femme' : 'Female'}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5"><span className="flex items-center gap-1.5"><Icon name="Tag" size={12} />{language === 'fr' ? 'Catégorie' : 'Resident Category'}</span></label>
                      <select value={checkInForm?.residentCategory} onChange={e => handleCheckInChange('residentCategory', e?.target?.value)} className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all">
                        <option value="">{language === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                        <option value="student">{language === 'fr' ? 'Étudiant' : 'Student'}</option>
                        <option value="staff">{language === 'fr' ? 'Personnel' : 'Staff'}</option>
                        <option value="faculty">{language === 'fr' ? 'Enseignant' : 'Faculty'}</option>
                        <option value="guest">{language === 'fr' ? 'Invité' : 'Guest'}</option>
                        <option value="researcher">{language === 'fr' ? 'Chercheur' : 'Researcher'}</option>
                      </select>
                    </div>
                  </div>

                  {/* Phone + Nationality */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5"><span className="flex items-center gap-1.5"><Icon name="Phone" size={12} />{language === 'fr' ? 'Téléphone' : 'Phone Number'}</span></label>
                      <input type="tel" value={checkInForm?.phone} onChange={e => handleCheckInChange('phone', e?.target?.value)} placeholder="+212 6XX XXX XXX" className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5"><span className="flex items-center gap-1.5"><Icon name="Globe" size={12} />{language === 'fr' ? 'Nationalité' : 'Nationality'}</span></label>
                      <input type="text" value={checkInForm?.nationality} onChange={e => handleCheckInChange('nationality', e?.target?.value)} placeholder={language === 'fr' ? 'ex. Marocaine' : 'e.g. Moroccan'} className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5"><span className="flex items-center gap-1.5"><Icon name="Mail" size={12} />{language === 'fr' ? 'Adresse e-mail' : 'Email Address'} <span className="text-red-500">*</span></span></label>
                    <input required type="email" value={checkInForm?.email} onChange={e => handleCheckInChange('email', e?.target?.value)} placeholder="e.g. ahmed.benali@example.com" className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" />
                  </div>

                  {/* Department + Financial Status */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5"><span className="flex items-center gap-1.5"><Icon name="Building2" size={12} />{language === 'fr' ? 'Département' : 'Department'}</span></label>
                      <input type="text" value={checkInForm?.department} onChange={e => handleCheckInChange('department', e?.target?.value)} placeholder={language === 'fr' ? 'ex. Sciences Politiques' : 'e.g. Political Science'} className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5"><span className="flex items-center gap-1.5"><Icon name="CreditCard" size={12} />{language === 'fr' ? 'Statut financier' : 'Financial Status'}</span></label>
                      <select value={checkInForm?.financialStatus} onChange={e => handleCheckInChange('financialStatus', e?.target?.value)} className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all">
                        <option value="">{language === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                        {FINANCIAL_STATUS_OPTIONS?.map(opt => <option key={opt?.value} value={opt?.value}>{language === 'fr' ? opt?.labelFr : opt?.labelEn}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end pt-2">
                    <button type="button" onClick={handleNextStep} disabled={!canProceedStep1} className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:opacity-90 transition-all disabled:opacity-40">
                      {language === 'fr' ? 'Suivant' : 'Next'} <Icon name="ArrowRight" size={15} />
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 2: Arrival Date ── */}
              {checkInStep === 2 && (
                <div className="space-y-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">2</div>
                    <h4 className="font-semibold text-foreground">{language === 'fr' ? 'Date d\'arrivée' : 'Arrival Date'}</h4>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                    <Icon name="Info" size={16} className="text-blue-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-blue-700">
                      {language === 'fr' ?'La date d\'arrivée détermine le début de la période de disponibilité. Les chambres seront filtrées en fonction de cette date.' :'The arrival date determines the start of the availability period. Rooms will be filtered based on this date.'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">{language === 'fr' ? 'Date d\'arrivée' : 'Arrival Date'} <span className="text-red-500">*</span></label>
                    <input
                      required
                      type="date"
                      value={checkInForm?.checkIn}
                      min={today}
                      onChange={e => handleCheckInChange('checkIn', e?.target?.value)}
                      className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                    />
                  </div>

                  {checkInForm?.checkIn && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
                      <Icon name="CalendarCheck" size={16} className="text-emerald-600" />
                      <span className="text-sm text-emerald-700 font-medium">
                        {language === 'fr' ? 'Arrivée prévue le' : 'Arrival scheduled for'}: {new Date(checkInForm?.checkIn + 'T00:00:00')?.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between pt-2">
                    <button type="button" onClick={handlePrevStep} className="flex items-center gap-2 px-5 py-2.5 bg-muted text-muted-foreground text-sm font-medium rounded-lg hover:bg-border transition-all">
                      <Icon name="ArrowLeft" size={15} /> {language === 'fr' ? 'Précédent' : 'Back'}
                    </button>
                    <button type="button" onClick={handleNextStep} disabled={!canProceedStep2} className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:opacity-90 transition-all disabled:opacity-40">
                      {language === 'fr' ? 'Suivant' : 'Next'} <Icon name="ArrowRight" size={15} />
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 3: Departure Date ── */}
              {checkInStep === 3 && (
                <div className="space-y-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">3</div>
                    <h4 className="font-semibold text-foreground">{language === 'fr' ? 'Date de départ' : 'Departure Date'}</h4>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                    <Icon name="Info" size={16} className="text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-amber-700">
                      {language === 'fr' ?'Après avoir sélectionné la date de départ, le système calculera automatiquement les chambres disponibles pour toute la période du séjour.' :'After selecting the departure date, the system will automatically calculate rooms available for the entire stay period.'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">{language === 'fr' ? 'Date de départ' : 'Departure Date'} <span className="text-red-500">*</span></label>
                    <input
                      required
                      type="date"
                      value={checkInForm?.checkOut}
                      min={checkInForm?.checkIn ? (() => { const d = new Date(checkInForm.checkIn + 'T00:00:00'); d?.setDate(d?.getDate() + 1); return d?.toISOString()?.split('T')?.[0]; })() : today}
                      onChange={e => handleCheckInChange('checkOut', e?.target?.value)}
                      className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                    />
                  </div>

                  {/* Stay summary */}
                  {checkInForm?.checkIn && checkInForm?.checkOut && canProceedStep3 && (
                    <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                      <h5 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{language === 'fr' ? 'Résumé du séjour' : 'Stay Summary'}</h5>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <Icon name="LogIn" size={18} className="text-blue-600 mx-auto mb-1" />
                          <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Arrivée' : 'Arrival'}</p>
                          <p className="text-sm font-bold text-foreground">{checkInForm?.checkIn}</p>
                        </div>
                        <div className="text-center p-3 bg-primary/5 border border-primary/20 rounded-lg">
                          <Icon name="CalendarDays" size={18} className="text-primary mx-auto mb-1" />
                          <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Durée' : 'Length of Stay'}</p>
                          <p className="text-xl font-bold text-primary">{lengthOfStay}</p>
                          <p className="text-xs text-muted-foreground">{language === 'fr' ? 'nuit(s)' : 'night(s)'}</p>
                        </div>
                        <div className="text-center p-3 bg-rose-50 border border-rose-200 rounded-lg">
                          <Icon name="LogOut" size={18} className="text-rose-600 mx-auto mb-1" />
                          <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Départ' : 'Departure'}</p>
                          <p className="text-sm font-bold text-foreground">{checkInForm?.checkOut}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between pt-2">
                    <button type="button" onClick={handlePrevStep} className="flex items-center gap-2 px-5 py-2.5 bg-muted text-muted-foreground text-sm font-medium rounded-lg hover:bg-border transition-all">
                      <Icon name="ArrowLeft" size={15} /> {language === 'fr' ? 'Précédent' : 'Back'}
                    </button>
                    <button type="button" onClick={handleNextStep} disabled={!canProceedStep3} className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:opacity-90 transition-all disabled:opacity-40">
                      {language === 'fr' ? 'Calculer la disponibilité' : 'Calculate Availability'} <Icon name="Search" size={15} />
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 4: Room Selection (date-driven) ── */}
              {checkInStep === 4 && (
                <div className="space-y-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">4</div>
                    <h4 className="font-semibold text-foreground">{language === 'fr' ? 'Sélection de chambre' : 'Room Selection'}</h4>
                  </div>

                  {/* Date range reminder */}
                  <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg text-sm">
                    <Icon name="CalendarRange" size={16} className="text-primary shrink-0" />
                    <span className="text-foreground font-medium">{checkInForm?.checkIn}</span>
                    <Icon name="ArrowRight" size={14} className="text-muted-foreground" />
                    <span className="text-foreground font-medium">{checkInForm?.checkOut}</span>
                    <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{lengthOfStay} {language === 'fr' ? 'nuit(s)' : 'night(s)'}</span>
                  </div>

                  {/* Accommodation Type */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-2"><span className="flex items-center gap-1.5"><Icon name="Home" size={12} />{language === 'fr' ? 'Type d\'hébergement' : 'Accommodation Type'} <span className="text-red-500">*</span></span></label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {ACCOMMODATION_TYPES?.map(type => (
                        <button key={type?.value} type="button" onClick={() => handleCheckInChange('accommodationType', type?.value)}
                          className={`flex items-start gap-3 p-3.5 rounded-lg border-2 text-left transition-all ${checkInForm?.accommodationType === type?.value ? (type?.value === 'long-term' ? 'border-blue-500 bg-blue-50' : 'border-emerald-500 bg-emerald-50') : 'border-border bg-background hover:border-primary/40'}`}>
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${type?.value === 'long-term' ? 'bg-blue-100' : 'bg-emerald-100'}`}>
                            <Icon name={type?.value === 'long-term' ? 'FileText' : 'CalendarDays'} size={16} className={type?.value === 'long-term' ? 'text-blue-600' : 'text-emerald-600'} />
                          </div>
                          <div>
                            <p className={`text-sm font-medium ${checkInForm?.accommodationType === type?.value ? (type?.value === 'long-term' ? 'text-blue-700' : 'text-emerald-700') : 'text-foreground'}`}>{language === 'fr' ? type?.labelFr : type?.labelEn}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{type?.value === 'long-term' ? (language === 'fr' ? 'Contrat + liste de contrôle requis' : 'Contract + checklist required') : (language === 'fr' ? 'Dates d\'entrée/sortie uniquement' : 'Check-in/out dates only')}</p>
                          </div>
                          {checkInForm?.accommodationType === type?.value && <Icon name="CheckCircle" size={16} className={`ml-auto shrink-0 ${type?.value === 'long-term' ? 'text-blue-500' : 'text-emerald-500'}`} />}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Building Selection */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-2">{language === 'fr' ? 'Sélectionner le bâtiment' : 'Select Building'} <span className="text-red-500">*</span></label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {Object.keys(BUILDINGS)?.map(b => {
                        const bRooms = BUILDINGS?.[b]?.floors?.flatMap(f => f?.rooms) ?? [];
                        const bAvail = bRooms?.filter(r => {
                          const s = getRoomAvailabilityStatus(r, checkInForm?.checkIn, checkInForm?.checkOut, realtime?.stays || [], rooms, roomTransfers);
                          return s?.available;
                        })?.length;
                        return (
                          <button key={b} type="button" onClick={() => handleBuildingSelect(b)}
                            className={`p-2.5 rounded-lg border-2 text-center transition-all ${selectedBuilding === b ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/40'}`}>
                            <p className="text-sm font-bold text-foreground">{b}</p>
                            <p className={`text-xs mt-0.5 font-medium ${bAvail === 0 ? 'text-red-500' : 'text-emerald-600'}`}>{bAvail} {language === 'fr' ? 'libre' : 'free'}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Room Grid */}
                  {selectedBuilding && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h5 className="text-sm font-semibold text-foreground">
                            {language === 'fr' ? `Chambres — Bâtiment ${selectedBuilding}` : `Rooms — Building ${selectedBuilding}`}
                          </h5>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            <span className="text-emerald-600 font-medium">{availableRoomsCount}</span> {language === 'fr' ? 'disponible(s)' : 'available'} / {totalRoomsInBuilding} {language === 'fr' ? 'total' : 'total'}
                          </p>
                        </div>
                        <div className="flex gap-1 bg-muted rounded-lg p-1">
                          <button type="button" onClick={() => setAvailabilityFilter('available')} className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${availabilityFilter === 'available' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}>
                            {language === 'fr' ? 'Disponibles' : 'Available'}
                          </button>
                          <button type="button" onClick={() => setAvailabilityFilter('all')} className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${availabilityFilter === 'all' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}>
                            {language === 'fr' ? 'Toutes' : 'All'}
                          </button>
                        </div>
                      </div>

                      {/* Legend */}
                      <div className="flex flex-wrap gap-3 mb-3 text-xs">
                        {[
                          { color: 'bg-emerald-100 border-emerald-300', label: language === 'fr' ? 'Disponible' : 'Available' },
                          { color: 'bg-red-100 border-red-200', label: language === 'fr' ? 'Occupée' : 'Occupied' },
                          { color: 'bg-amber-100 border-amber-200', label: language === 'fr' ? 'Réservée' : 'Reserved' },
                          { color: 'bg-orange-100 border-orange-200', label: language === 'fr' ? 'Maintenance' : 'Maintenance' },
                          { color: 'bg-purple-100 border-purple-200', label: language === 'fr' ? 'Transfert' : 'Transfer' },
                        ]?.map(l => (
                          <div key={l?.label} className="flex items-center gap-1.5">
                            <div className={`w-3 h-3 rounded border ${l?.color}`} />
                            <span className="text-muted-foreground">{l?.label}</span>
                          </div>
                        ))}
                      </div>

                      {displayedRooms?.length === 0 ? (
                        <div className="text-center py-8 bg-muted/30 rounded-xl border border-border">
                          <Icon name="BedDouble" size={32} className="text-muted-foreground mx-auto mb-2" />
                          <p className="text-sm font-medium text-foreground">{language === 'fr' ? 'Aucune chambre disponible' : 'No rooms available'}</p>
                          <p className="text-xs text-muted-foreground mt-1">{language === 'fr' ? 'Toutes les chambres sont occupées ou réservées pour cette période.' : 'All rooms are occupied or reserved for this period.'}</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-72 overflow-y-auto pr-1">
                          {displayedRooms?.map(({ roomNumber, availStatus }) => (
                            <RoomAvailabilityCard
                              key={roomNumber}
                              roomNumber={roomNumber}
                              availStatus={availStatus}
                              selected={checkInForm?.room === roomNumber}
                              onSelect={handleRoomSelect}
                              language={language}
                            />
                          ))}
                        </div>
                      )}

                      {checkInForm?.room && (
                        <div className="mt-3 flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <Icon name="CheckCircle" size={16} className="text-emerald-600" />
                          <span className="text-sm text-emerald-700 font-medium">
                            {language === 'fr' ? 'Chambre sélectionnée :' : 'Selected room:'} <strong>{checkInForm?.room}</strong>
                          </span>
                          <button type="button" onClick={() => handleCheckInChange('room', '')} className="ml-auto text-emerald-500 hover:text-emerald-700">
                            <Icon name="X" size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">{language === 'fr' ? 'Notes / Demandes spéciales (optionnel)' : 'Notes / Special Requests (optional)'}</label>
                    <textarea value={checkInForm?.notes} onChange={e => handleCheckInChange('notes', e?.target?.value)} rows={2} placeholder={language === 'fr' ? 'Exigences particulières...' : 'Any special requirements...'} className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none" />
                  </div>

                  <div className="flex justify-between pt-2">
                    <button type="button" onClick={handlePrevStep} className="flex items-center gap-2 px-5 py-2.5 bg-muted text-muted-foreground text-sm font-medium rounded-lg hover:bg-border transition-all">
                      <Icon name="ArrowLeft" size={15} /> {language === 'fr' ? 'Précédent' : 'Back'}
                    </button>
                    <button type="button" onClick={handleNextStep} disabled={!canProceedStep4} className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:opacity-90 transition-all disabled:opacity-40">
                      {language === 'fr' ? 'Vérifier et confirmer' : 'Review & Confirm'} <Icon name="ArrowRight" size={15} />
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 5: Confirm ── */}
              {checkInStep === 5 && (
                <form onSubmit={handleCheckInSubmit} className="space-y-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">5</div>
                    <h4 className="font-semibold text-foreground">{language === 'fr' ? 'Confirmer l\'enregistrement' : 'Confirm Check-in'}</h4>
                  </div>

                  {/* Summary card */}
                  <div className="bg-muted/30 border border-border rounded-xl p-5 space-y-4">
                    <h5 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{language === 'fr' ? 'Récapitulatif complet' : 'Complete Summary'}</h5>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase">{language === 'fr' ? 'Résident' : 'Resident'}</p>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm"><Icon name="User" size={13} className="text-muted-foreground" /><span className="font-medium">{checkInForm?.residentName}</span></div>
                          {checkInForm?.email && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon name="Mail" size={12} /><span>{checkInForm?.email}</span></div>}
                          {checkInForm?.phone && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon name="Phone" size={12} /><span>{checkInForm?.phone}</span></div>}
                          {checkInForm?.nationality && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon name="Globe" size={12} /><span>{checkInForm?.nationality}</span></div>}
                          {checkInForm?.department && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon name="Building2" size={12} /><span>{checkInForm?.department}</span></div>}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase">{language === 'fr' ? 'Séjour' : 'Stay'}</p>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm"><Icon name="Home" size={13} className="text-muted-foreground" /><span className="font-mono font-bold text-primary">{checkInForm?.room}</span><span className="text-muted-foreground text-xs">({checkInForm?.building})</span></div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon name="LogIn" size={12} /><span>{checkInForm?.checkIn}</span><Icon name="ArrowRight" size={10} /><span>{checkInForm?.checkOut}</span></div>
                          <div className="flex items-center gap-2 text-xs"><Icon name="CalendarDays" size={12} className="text-primary" /><span className="font-medium text-primary">{lengthOfStay} {language === 'fr' ? 'nuit(s)' : 'night(s)'}</span></div>
                          {checkInForm?.accommodationType && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon name={checkInForm?.accommodationType === 'long-term' ? 'FileText' : 'CalendarDays'} size={12} /><span>{accommodationLabel(checkInForm?.accommodationType)}</span></div>}
                          {checkInForm?.financialStatus && <div className="flex items-center gap-2 text-xs"><Icon name="CreditCard" size={12} className={checkInForm?.financialStatus === 'covered' ? 'text-emerald-600' : 'text-rose-600'} /><span className={`font-medium ${checkInForm?.financialStatus === 'covered' ? 'text-emerald-600' : 'text-rose-600'}`}>{financialStatusLabel(checkInForm?.financialStatus)}</span></div>}
                        </div>
                      </div>
                    </div>

                    {/* Availability confirmation */}
                    <div className="pt-3 border-t border-border">
                      {(() => {
                        const finalCheck = getRoomAvailabilityStatus(checkInForm?.room, checkInForm?.checkIn, checkInForm?.checkOut, realtime?.stays || [], rooms, roomTransfers);
                        return finalCheck?.available ? (
                          <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                            <Icon name="ShieldCheck" size={16} className="text-emerald-600" />
                            <div>
                              <p className="text-sm font-medium text-emerald-700">{language === 'fr' ? 'Chambre validée et disponible' : 'Room validated and available'}</p>
                              <p className="text-xs text-emerald-600">{language === 'fr' ? 'Aucun conflit détecté pour la période sélectionnée.' : 'No conflicts detected for the selected period.'}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <Icon name="ShieldAlert" size={16} className="text-red-600" />
                            <div>
                              <p className="text-sm font-medium text-red-700">{language === 'fr' ? 'Conflit de disponibilité détecté !' : 'Availability conflict detected!'}</p>
                              <p className="text-xs text-red-600">{language === 'fr' ? `Raison: ${finalCheck?.label}. Veuillez revenir et sélectionner une autre chambre.` : `Reason: ${finalCheck?.label}. Please go back and select another room.`}</p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="flex justify-between pt-2">
                    <button type="button" onClick={handlePrevStep} className="flex items-center gap-2 px-5 py-2.5 bg-muted text-muted-foreground text-sm font-medium rounded-lg hover:bg-border transition-all">
                      <Icon name="ArrowLeft" size={15} /> {language === 'fr' ? 'Précédent' : 'Back'}
                    </button>
                    <div className="flex gap-3">
                      <button type="button" onClick={() => { setCheckInForm(emptyCheckIn); setCheckInStep(1); setSelectedBuilding(''); setEmailStatus(null); }} className="flex items-center gap-2 px-5 py-2.5 bg-muted text-muted-foreground text-sm font-medium rounded-lg hover:bg-border transition-all">
                        <Icon name="RotateCcw" size={15} /> {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                      </button>
                      <button type="submit" disabled={submitting || !checkInForm?.room} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-all disabled:opacity-60">
                        {submitting ? <Icon name="Loader" size={15} className="animate-spin" /> : <Icon name="LogIn" size={15} />}
                        {language === 'fr' ? 'Confirmer l\'enregistrement' : 'Confirm Check-in'}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Availability summary */}
              <div className="bg-card border border-border rounded-xl p-5">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2"><Icon name="BarChart2" size={15} className="text-primary" />{language === 'fr' ? 'Disponibilité par bâtiment' : 'Availability by Building'}</h4>
                {checkInForm?.checkIn && checkInForm?.checkOut && canProceedStep3 ? (
                  <div className="space-y-2">
                    {Object.keys(BUILDINGS)?.map(b => {
                      const bRooms = BUILDINGS?.[b]?.floors?.flatMap(f => f?.rooms) ?? [];
                      const bAvail = bRooms?.filter(r => getRoomAvailabilityStatus(r, checkInForm?.checkIn, checkInForm?.checkOut, realtime?.stays || [], rooms, roomTransfers)?.available)?.length;
                      const pct = bRooms?.length > 0 ? Math.round(((bRooms?.length - bAvail) / bRooms?.length) * 100) : 0;
                      return (
                        <div key={b}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="font-medium text-foreground">{b}</span>
                            <span className={`font-medium ${bAvail === 0 ? 'text-red-500' : 'text-emerald-600'}`}>{bAvail} {language === 'fr' ? 'libre' : 'free'} / {bRooms?.length}</span>
                          </div>
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <Icon name="Info" size={11} />
                      {language === 'fr' ? `Pour la période ${checkInForm?.checkIn} → ${checkInForm?.checkOut}` : `For period ${checkInForm?.checkIn} → ${checkInForm?.checkOut}`}
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Sélectionnez les dates d\'arrivée et de départ pour voir la disponibilité.' : 'Select arrival and departure dates to see availability.'}</p>
                )}
              </div>

              {/* Availability rules info */}
              <div className="bg-card border border-border rounded-xl p-5">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2"><Icon name="ShieldCheck" size={15} className="text-emerald-600" />{language === 'fr' ? 'Règles de disponibilité' : 'Availability Rules'}</h4>
                <div className="space-y-2 text-xs text-muted-foreground">
                  {[
                    { icon: 'UserCheck', color: 'text-red-500', label: language === 'fr' ? 'Chambres occupées exclues' : 'Occupied rooms excluded' },
                    { icon: 'Clock', color: 'text-amber-500', label: language === 'fr' ? 'Réservations chevauchantes exclues' : 'Overlapping reservations excluded' },
                    { icon: 'Wrench', color: 'text-orange-500', label: language === 'fr' ? 'Chambres en maintenance exclues' : 'Maintenance rooms excluded' },
                    { icon: 'ArrowLeftRight', color: 'text-purple-500', label: language === 'fr' ? 'Transferts de chambre exclus' : 'Room transfers excluded' },
                    { icon: 'Shield', color: 'text-blue-500', label: language === 'fr' ? 'Double réservation impossible' : 'Double booking prevented' },
                  ]?.map(r => (
                    <div key={r?.label} className="flex items-center gap-2">
                      <Icon name={r?.icon} size={12} className={r?.color} />
                      <span>{r?.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Current step progress */}
              <div className="bg-card border border-border rounded-xl p-5">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2"><Icon name="ListChecks" size={15} className="text-primary" />{language === 'fr' ? 'Progression' : 'Progress'}</h4>
                <div className="space-y-2">
                  {[
                    { step: 1, label: language === 'fr' ? 'Infos résident' : 'Resident info', done: checkInStep > 1 },
                    { step: 2, label: language === 'fr' ? 'Date d\'arrivée' : 'Arrival date', done: checkInStep > 2 },
                    { step: 3, label: language === 'fr' ? 'Date de départ' : 'Departure date', done: checkInStep > 3 },
                    { step: 4, label: language === 'fr' ? 'Chambre sélectionnée' : 'Room selected', done: checkInStep > 4 },
                    { step: 5, label: language === 'fr' ? 'Confirmation' : 'Confirmation', done: false },
                  ]?.map(s => (
                    <div key={s?.step} className={`flex items-center gap-2 text-xs ${checkInStep === s?.step ? 'text-primary font-medium' : s?.done ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                      <Icon name={s?.done ? 'CheckCircle' : checkInStep === s?.step ? 'Circle' : 'Circle'} size={12} className={s?.done ? 'text-emerald-500' : checkInStep === s?.step ? 'text-primary' : 'text-muted-foreground'} />
                      {s?.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── CHECK-OUT FORM ────────────────────────────────────────────────── */}
        {activeTab === 'checkout' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-5">
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center">
                    <Icon name="LogOut" size={18} className="text-rose-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{language === 'fr' ? 'Traiter le départ' : 'Process Check-out'}</h3>
                    <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Enregistrer le départ d\'un résident' : 'Record a resident departure'}</p>
                  </div>
                </div>

                {checkOutSuccess && (
                  <div className="mb-5 flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <Icon name="CheckCircle" size={18} className="text-blue-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-800">{language === 'fr' ? 'Départ traité avec succès !' : 'Check-out processed successfully!'}</p>
                      <p className="text-xs text-blue-700 mt-0.5">{checkOutSuccess?.residentName} {language === 'fr' ? 'a quitté la chambre' : 'checked out from'} {checkOutSuccess?.room} {language === 'fr' ? 'le' : 'on'} {checkOutSuccess?.actualCheckOut}</p>
                      <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                        <Icon name="RefreshCw" size={11} />
                        {language === 'fr' ? 'Chambre libérée. Tous les modules mis à jour automatiquement.' : 'Room released. All modules updated automatically.'}
                      </p>
                      {checkOutSuccess?.claim && (
                        <div className="mt-2 flex items-center gap-2">
                          <Icon name="FileWarning" size={12} className="text-amber-600" />
                          <span className="text-xs text-amber-700 font-medium">{language === 'fr' ? 'Rapport de réclamation créé.' : 'Claim report created.'}</span>
                          <button onClick={() => handleDownloadClaimPDF(checkOutSuccess?.claim)} disabled={pdfGenerating} className="ml-2 flex items-center gap-1 text-xs px-2.5 py-1 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-all disabled:opacity-60">
                            <Icon name={pdfGenerating ? 'Loader' : 'Download'} size={11} className={pdfGenerating ? 'animate-spin' : ''} />
                            {language === 'fr' ? 'Télécharger PDF' : 'Download PDF'}
                          </button>
                        </div>
                      )}
                    </div>
                    <button onClick={() => setCheckOutSuccess(null)} className="ml-auto text-blue-500 hover:text-blue-700"><Icon name="X" size={14} /></button>
                  </div>
                )}

                {checkOutError && (
                  <div className="mb-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
                    <Icon name="AlertCircle" size={18} className="text-red-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-800">{language === 'fr' ? 'Erreur lors du départ' : 'Check-out failed'}</p>
                      <p className="text-xs text-red-700 mt-0.5">{checkOutError}</p>
                    </div>
                    <button onClick={() => setCheckOutError(null)} className="ml-auto text-red-400 hover:text-red-600"><Icon name="X" size={14} /></button>
                  </div>
                )}

                <form onSubmit={handleCheckOutSubmit} className="space-y-5">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">{language === 'fr' ? 'Sélectionner le séjour actif' : 'Select Active Stay'} <span className="text-red-500">*</span></label>
                    <select required value={checkOutForm?.stayId} onChange={e => setCheckOutForm(prev => ({ ...prev, stayId: e?.target?.value }))} className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all">
                      <option value="">{language === 'fr' ? 'Sélectionner résident / séjour' : 'Select resident / stay'}</option>
                      {stays?.filter(s => s?.status === 'confirmed-stay' || s?.status === 'active-reservation' || s?.status === 'active' || s?.status === 'pending')?.map(s => (
                        <option key={s?.id} value={s?.id}>{s?.residentName} — {s?.room} ({language === 'fr' ? 'depuis' : 'since'} {s?.checkIn})</option>
                      ))}
                    </select>
                  </div>

                  {checkOutForm?.stayId && (() => {
                    const stay = stays?.find(s => s?.id === checkOutForm?.stayId);
                    return stay ? (
                      <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {[
                            { label: language === 'fr' ? 'Résident' : 'Resident', value: stay?.residentName },
                            { label: 'ID', value: stay?.residentId },
                            { label: language === 'fr' ? 'Chambre' : 'Room', value: stay?.room },
                            { label: language === 'fr' ? 'Arrivée' : 'Check-in', value: stay?.checkIn },
                          ]?.map((item, i) => (
                            <div key={i}>
                              <p className="text-xs text-muted-foreground">{item?.label}</p>
                              <p className="text-sm font-medium text-foreground">{item?.value}</p>
                            </div>
                          ))}
                        </div>
                        {(stay?.department || stay?.financialStatus) && (
                          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
                            {stay?.department && <div><p className="text-xs text-muted-foreground">{language === 'fr' ? 'Département' : 'Department'}</p><p className="text-sm font-medium text-foreground">{stay?.department}</p></div>}
                            {stay?.financialStatus && <div><p className="text-xs text-muted-foreground">{language === 'fr' ? 'Statut financier' : 'Financial Status'}</p><p className="text-sm font-medium text-foreground">{financialStatusLabel(stay?.financialStatus)}</p></div>}
                          </div>
                        )}
                      </div>
                    ) : null;
                  })()}

                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">{language === 'fr' ? 'Date de départ réelle' : 'Actual Check-out Date'} <span className="text-red-500">*</span></label>
                    <input required type="date" value={checkOutForm?.actualCheckOut} onChange={e => setCheckOutForm(prev => ({ ...prev, actualCheckOut: e?.target?.value }))} className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" />
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{language === 'fr' ? 'Vérification du départ' : 'Check-out Verification'}</p>
                    <div className="flex items-center gap-3 p-3 bg-muted/40 border border-border rounded-lg">
                      <input type="checkbox" id="keyReturned" checked={checkOutForm?.keyReturned} onChange={e => setCheckOutForm(prev => ({ ...prev, keyReturned: e?.target?.checked }))} className="w-4 h-4 rounded border-border text-primary focus:ring-primary/30" />
                      <label htmlFor="keyReturned" className="text-sm text-foreground cursor-pointer flex items-center gap-2"><Icon name="Key" size={14} className="text-muted-foreground" />{language === 'fr' ? 'Clé / carte d\'accès rendue' : 'Key / Access Card Returned'}</label>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/40 border border-border rounded-lg">
                      <input type="checkbox" id="roomCheckCompleted" checked={checkOutForm?.roomCheckCompleted} onChange={e => setCheckOutForm(prev => ({ ...prev, roomCheckCompleted: e?.target?.checked }))} className="w-4 h-4 rounded border-border text-primary focus:ring-primary/30" />
                      <label htmlFor="roomCheckCompleted" className="text-sm text-foreground cursor-pointer flex items-center gap-2"><Icon name="ClipboardCheck" size={14} className="text-muted-foreground" />{language === 'fr' ? 'Vérification de la chambre effectuée' : 'Room Check Completed'}</label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">{language === 'fr' ? 'Notes de départ (optionnel)' : 'Departure Notes (optional)'}</label>
                    <textarea value={checkOutForm?.notes} onChange={e => setCheckOutForm(prev => ({ ...prev, notes: e?.target?.value }))} rows={3} placeholder={language === 'fr' ? 'État de la chambre, dommages, remarques...' : 'Room condition, damages, remarks...'} className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none" />
                  </div>

                  <div className="flex gap-3 pt-1">
                    <button type="submit" disabled={submitting} className="flex items-center gap-2 px-5 py-2.5 bg-rose-600 text-white text-sm font-medium rounded-lg hover:bg-rose-700 transition-all disabled:opacity-60">
                      {submitting ? <Icon name="Loader" size={15} className="animate-spin" /> : <Icon name="LogOut" size={15} />}
                      {language === 'fr' ? 'Traiter le départ' : 'Process Check-out'}
                    </button>
                    <button type="button" onClick={() => setCheckOutForm(emptyCheckOut)} className="flex items-center gap-2 px-5 py-2.5 bg-muted text-muted-foreground text-sm font-medium rounded-lg hover:bg-border transition-all">
                      <Icon name="RotateCcw" size={15} /> {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Claim Section */}
              <div className="bg-card border border-amber-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center"><Icon name="FileWarning" size={18} className="text-amber-600" /></div>
                    <div>
                      <h3 className="font-semibold text-foreground">{language === 'fr' ? 'Rapport de réclamation / incident' : 'Claim / Issue Report'}</h3>
                      <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Signaler un problème détecté dans la chambre' : 'Report a problem detected in the room'}</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setCheckOutForm(prev => ({ ...prev, claimEnabled: !prev?.claimEnabled, claimDescription: '', claimPhotos: [], claimPhotoFiles: [] }))}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all ${checkOutForm?.claimEnabled ? 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200' : 'bg-muted text-muted-foreground border-border hover:bg-border'}`}>
                    <Icon name={checkOutForm?.claimEnabled ? 'ChevronUp' : 'Plus'} size={14} />
                    {checkOutForm?.claimEnabled ? (language === 'fr' ? 'Masquer' : 'Hide') : (language === 'fr' ? 'Créer un rapport' : 'Create Report')}
                  </button>
                </div>

                {checkOutForm?.claimEnabled && (
                  <div className="space-y-4 pt-2 border-t border-amber-100">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-xs text-amber-600 font-medium">{language === 'fr' ? 'Date du rapport' : 'Report Date'}</p>
                        <p className="text-sm font-semibold text-amber-800 mt-0.5">{today}</p>
                        <p className="text-xs text-amber-500 mt-0.5">{language === 'fr' ? 'Généré automatiquement' : 'Auto-generated'}</p>
                      </div>
                      {checkOutForm?.stayId && (() => {
                        const stay = stays?.find(s => s?.id === checkOutForm?.stayId);
                        return stay ? (
                          <>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <p className="text-xs text-amber-600 font-medium">{language === 'fr' ? 'Chambre' : 'Room Number'}</p>
                              <p className="text-sm font-semibold text-amber-800 mt-0.5">{stay?.room}</p>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <p className="text-xs text-amber-600 font-medium">{language === 'fr' ? 'Résident' : 'Guest Name'}</p>
                              <p className="text-sm font-semibold text-amber-800 mt-0.5 truncate">{stay?.residentName}</p>
                            </div>
                          </>
                        ) : null;
                      })()}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5"><span className="flex items-center gap-1.5"><Icon name="FileText" size={12} />{language === 'fr' ? 'Description du problème' : 'Issue Description'} <span className="text-red-500">*</span></span></label>
                      <textarea value={checkOutForm?.claimDescription} onChange={e => setCheckOutForm(prev => ({ ...prev, claimDescription: e?.target?.value }))} rows={4} placeholder={language === 'fr' ? 'Décrivez le problème ou dommage constaté...' : 'Describe the issue or damage found...'} className="w-full px-3 py-2.5 text-sm bg-background border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400 transition-all resize-none" />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5"><span className="flex items-center gap-1.5"><Icon name="Camera" size={12} />{language === 'fr' ? 'Joindre des photos' : 'Attach Photos'}</span></label>
                      <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-amber-300 rounded-lg cursor-pointer bg-amber-50 hover:bg-amber-100 transition-all">
                        <div className="flex flex-col items-center gap-1">
                          <Icon name="Upload" size={20} className="text-amber-500" />
                          <p className="text-xs text-amber-600 font-medium">{language === 'fr' ? 'Cliquer pour télécharger des photos' : 'Click to upload photos'}</p>
                          <p className="text-xs text-amber-400">PNG, JPG, JPEG</p>
                        </div>
                        <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
                      </label>
                      {checkOutForm?.claimPhotos?.length > 0 && (
                        <div className="mt-3 grid grid-cols-4 gap-2">
                          {checkOutForm?.claimPhotos?.map((photo, idx) => (
                            <div key={idx} className="relative group">
                              <img src={photo} alt={`Claim photo ${idx + 1}`} className="w-full h-16 object-cover rounded-lg border border-amber-200" />
                              <button type="button" onClick={() => removePhoto(idx)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Icon name="X" size={10} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Active stays list */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2"><Icon name="Users" size={15} className="text-primary" />{language === 'fr' ? 'Séjours actifs' : 'Active Stays'} ({activeStays?.length})</h4>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {activeStays?.length === 0
                  ? <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Aucun séjour actif' : 'No active stays'}</p>
                  : activeStays?.map(s => (
                    <div key={s?.id} onClick={() => setCheckOutForm(prev => ({ ...prev, stayId: String(s?.id) }))}
                      className={`p-3 rounded-lg border cursor-pointer transition-all hover:border-primary/50 ${checkOutForm?.stayId === String(s?.id) ? 'border-primary bg-primary/5' : 'border-border'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">{s?.residentName}</p>
                          <p className="text-xs text-muted-foreground">{s?.room}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full border shrink-0 ${STATUS_CONFIG?.[s?.status]?.color}`}>{statusLabels?.[STATUS_CONFIG?.[s?.status]?.labelKey] || s?.status}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{language === 'fr' ? 'Jusqu\'au' : 'Until'} {s?.checkOut}</p>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        )}

        {/* ─── GUEST STATUS TAB ──────────────────────────────────────────────── */}
        {activeTab === 'guest-status' && (
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 items-start sm:items-center">
              <div className="relative flex-1 max-w-xs">
                <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e?.target?.value)} placeholder={language === 'fr' ? 'Rechercher résident ou chambre...' : 'Search resident or room...'} className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" />
              </div>
              <div className="flex gap-1 bg-muted rounded-lg p-1 flex-wrap">
                {[
                  { id: 'all', label: language === 'fr' ? 'Tous' : 'All' },
                  { id: 'awaiting-arrival', label: language === 'fr' ? 'En attente' : 'Awaiting' },
                  { id: 'checked-in', label: language === 'fr' ? 'Enregistré' : 'Checked In' },
                  { id: 'checked-out', label: language === 'fr' ? 'Parti' : 'Checked Out' },
                  { id: 'extended', label: language === 'fr' ? 'Prolongé' : 'Extended' },
                  { id: 'early-departure', label: language === 'fr' ? 'Départ anticipé' : 'Early Departure' },
                ]?.map(f => (
                  <button key={f?.id} onClick={() => setGuestStatusFilter(f?.id)} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${guestStatusFilter === f?.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>{f?.label}</button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
              <Icon name="MousePointerClick" size={13} />
              <span>{language === 'fr' ? 'Cliquez sur une carte pour voir les détails complets du hôte' : 'Click any card to view full guest details'}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredGuestStatus?.length === 0 ? (
                <div className="col-span-3 text-center py-12 text-muted-foreground text-sm">{language === 'fr' ? 'Aucun hôte trouvé' : 'No guests found'}</div>
              ) : filteredGuestStatus?.map(s => (
                <div key={s?.id} onClick={() => setSelectedGuest(s)} className="bg-card border border-border rounded-xl p-5 hover:shadow-lg hover:border-primary/40 transition-all duration-200 cursor-pointer group relative" title={language === 'fr' ? 'Cliquer pour voir les détails' : 'Click to view details'}>
                  <div className="absolute top-3 right-3 w-6 h-6 rounded-md bg-muted flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Icon name="Maximize2" size={12} className="text-muted-foreground" /></div>
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors"><Icon name="User" size={18} className="text-primary" /></div>
                      <div>
                        <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{s?.residentName}</p>
                        <p className="text-xs text-muted-foreground">{s?.residentId}</p>
                        {s?.guestId && <p className="text-xs text-primary/70 font-mono">GID: {s?.guestId}</p>}
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-medium shrink-0 ${GUEST_STATUS_CONFIG?.[s?.guestStatus]?.color || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${GUEST_STATUS_CONFIG?.[s?.guestStatus]?.dot || 'bg-slate-400'}`} />
                      {guestStatusLabels?.[s?.guestStatus] || s?.guestStatus}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs"><Icon name="Home" size={12} className="text-muted-foreground shrink-0" /><span className="text-muted-foreground">{language === 'fr' ? 'Chambre' : 'Room'}:</span><span className="font-mono font-medium text-foreground">{s?.room}</span><span className="text-muted-foreground">({s?.building})</span></div>
                    {s?.department && <div className="flex items-center gap-2 text-xs"><Icon name="Building2" size={12} className="text-muted-foreground shrink-0" /><span className="text-muted-foreground">{s?.department}</span></div>}
                    {s?.financialStatus && <div className="flex items-center gap-2 text-xs"><Icon name="CreditCard" size={12} className="text-muted-foreground shrink-0" /><span className={`font-medium ${s?.financialStatus === 'covered' ? 'text-emerald-600' : 'text-rose-600'}`}>{financialStatusLabel(s?.financialStatus)}</span></div>}
                    {s?.email && <div className="flex items-center gap-2 text-xs"><Icon name="Mail" size={12} className="text-muted-foreground shrink-0" /><span className="text-muted-foreground truncate">{s?.email}</span></div>}
                    <div className="flex items-center gap-2 text-xs"><Icon name="CalendarDays" size={12} className="text-muted-foreground shrink-0" /><span className="text-muted-foreground">{s?.checkIn}</span><Icon name="ArrowRight" size={10} className="text-muted-foreground" /><span className="text-muted-foreground">{s?.checkOut}</span></div>
                    <div className="flex items-center gap-2 text-xs pt-1 border-t border-border">
                      <Icon name={s?.accommodationType === 'long-term' ? 'FileText' : 'CalendarDays'} size={12} className={s?.accommodationType === 'long-term' ? 'text-blue-500' : 'text-emerald-500'} />
                      <span className={`font-medium ${s?.accommodationType === 'long-term' ? 'text-blue-600' : 'text-emerald-600'}`}>{accommodationLabel(s?.accommodationType)}</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-border flex items-center justify-center gap-1.5 text-xs text-muted-foreground group-hover:text-primary transition-colors">
                    <Icon name="Eye" size={12} />
                    <span>{language === 'fr' ? 'Voir les détails complets' : 'View full details'}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-xs text-muted-foreground px-1">{language === 'fr' ? `Affichage de ${filteredGuestStatus?.length} sur ${stays?.length} hôtes` : `Showing ${filteredGuestStatus?.length} of ${stays?.length} guests`}</div>
          </div>
        )}

        {/* ─── STATUS TRACKER ────────────────────────────────────────────────── */}
        {activeTab === 'tracker' && (
          <div className="bg-card border border-border rounded-xl">
            <div className="p-5 border-b border-border flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-3 flex-1">
                <div className="relative flex-1 max-w-xs">
                  <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input type="text" value={searchQuery} onChange={e => setSearchQuery(e?.target?.value)} placeholder={language === 'fr' ? 'Rechercher résident, chambre, ID...' : 'Search resident, room, ID...'} className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" />
                </div>
              </div>
              <div className="flex gap-1 bg-muted rounded-lg p-1">
                {[
                  { id: 'all', label: language === 'fr' ? 'Tous' : 'All' },
                  { id: 'active-reservation', label: language === 'fr' ? 'Réservation active' : 'Active Reservation' },
                  { id: 'confirmed-stay', label: language === 'fr' ? 'Séjour confirmé' : 'Confirmed Stay' },
                  { id: 'completed-stay', label: language === 'fr' ? 'Séjour terminé' : 'Completed Stay' },
                ]?.map(f => (
                  <button key={f?.id} onClick={() => setStatusFilter(f?.id)} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${statusFilter === f?.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>{f?.label}</button>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    {[
                      language === 'fr' ? 'Résident' : 'Resident',
                      'ID',
                      language === 'fr' ? 'Chambre' : 'Room',
                      language === 'fr' ? 'Bâtiment' : 'Building',
                      language === 'fr' ? 'Département' : 'Department',
                      language === 'fr' ? 'Statut financier' : 'Financial Status',
                      language === 'fr' ? 'Arrivée' : 'Check-in',
                      language === 'fr' ? 'Départ' : 'Check-out',
                      language === 'fr' ? 'Durée' : 'Duration',
                      language === 'fr' ? 'Statut hôte' : 'Guest Status',
                      language === 'fr' ? 'Statut séjour' : 'Stay Status',
                    ]?.map(h => (
                      <th key={h} className="text-left text-xs font-medium text-muted-foreground px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredStays?.length === 0 ? (
                    <tr><td colSpan={11} className="text-center text-muted-foreground py-10 text-sm">{language === 'fr' ? 'Aucun enregistrement trouvé' : 'No records found'}</td></tr>
                  ) : filteredStays?.map(s => {
                    const nights = s?.checkIn && s?.checkOut ? Math.round((new Date(s?.checkOut) - new Date(s?.checkIn)) / (1000 * 60 * 60 * 24)) : null;
                    return (
                      <tr key={s?.id} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Icon name="User" size={13} className="text-primary" /></div>
                            <div>
                              <span className="font-medium text-foreground block">{s?.residentName}</span>
                              {s?.email && <span className="text-xs text-muted-foreground">{s?.email}</span>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{s?.residentId}</td>
                        <td className="px-4 py-3 font-mono text-xs font-medium text-foreground">{s?.room}</td>
                        <td className="px-4 py-3 text-muted-foreground">{s?.building}</td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">{s?.department || '—'}</td>
                        <td className="px-4 py-3">
                          {s?.financialStatus ? <span className={`text-xs font-medium ${s?.financialStatus === 'covered' ? 'text-emerald-600' : 'text-rose-600'}`}>{financialStatusLabel(s?.financialStatus)}</span> : <span className="text-muted-foreground text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{s?.checkIn}</td>
                        <td className="px-4 py-3 text-muted-foreground">{s?.checkOut}</td>
                        <td className="px-4 py-3">
                          {nights !== null ? <span className="text-xs font-medium text-primary">{nights} {language === 'fr' ? 'n.' : 'n.'}</span> : <span className="text-muted-foreground text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-medium ${GUEST_STATUS_CONFIG?.[s?.guestStatus]?.color || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${GUEST_STATUS_CONFIG?.[s?.guestStatus]?.dot || 'bg-slate-400'}`} />
                            {guestStatusLabels?.[s?.guestStatus] || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-medium ${STATUS_CONFIG?.[s?.status]?.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${STATUS_CONFIG?.[s?.status]?.dot}`} />
                            {statusLabels?.[STATUS_CONFIG?.[s?.status]?.labelKey] || s?.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">
              {language === 'fr' ? `Affichage de ${filteredStays?.length} sur ${stays?.length} enregistrements` : `Showing ${filteredStays?.length} of ${stays?.length} records`}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
